import { Button } from "@/components/flexkit/Button";
import { Nudge } from "@/components/nudges/Nudge";
import { Text } from "@/components/podkit/typography/Text";
import { NudgeKeys, useNudges } from "@/hooks/use-nudges";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useSetPreference, useListPreferences } from "@/queries/user-preferences-queries";
import { useGetAccount } from "@/queries/account-queries";
import { useCallback, useEffect, useState, type FC } from "react";
import { ExternalLink } from "@/components/podkit/typography/Link";
import { toast } from "@/components/podkit/toasts/use-toast";
import { IconExternalLink } from "@/assets/icons/geist/IconExternalLink";
import { OnaWordmark } from "@/assets/icons/OnaWordmark";

export const ONA_WAITLIST_KEY = "ONA_WAITLIST_JOINED";

export const OnaAnnouncementNudge: FC = () => {
    const { active: nudge, error, isPending } = useNudges();
    const { mutate: setPreference } = useSetPreference();
    const { preferences } = useListPreferences();
    const { data: account } = useGetAccount();
    const [open, setOpen] = useState(false);
    const [waitListJoined, setWaitListJoined] = useState(false);
    const segmentTrack = useSegmentTrack();

    // Check if user has already joined waitlist
    useEffect(() => {
        if (!preferences) return;
        const hasJoined = preferences.find((pref) => pref.key === ONA_WAITLIST_KEY)?.value === "true";
        setWaitListJoined(hasJoined);
    }, [preferences]);

    // Auto-show nudge if applicable
    useEffect(() => {
        if (isPending || error) {
            return;
        }

        if (nudge?.key === NudgeKeys.ONA_ANNOUNCEMENT) {
            setOpen(true);
            return;
        }
        setOpen(false);
    }, [isPending, error, nudge?.key]);

    const toggleOpen = useCallback(() => {
        setOpen((previousOpenState) => {
            const isNowOpen = !previousOpenState;
            if (!isNowOpen) {
                setPreference({
                    key: NudgeKeys.ONA_ANNOUNCEMENT,
                    value: new Date().toISOString(),
                });
            }
            return isNowOpen;
        });
    }, [setPreference]);

    const onJoinWaitlist = useCallback(() => {
        segmentTrack("waitlist_joined", { feature: "Ona", email: account?.email });
        setWaitListJoined(true);
        // Store waitlist joined state in preferences
        setPreference({
            key: ONA_WAITLIST_KEY,
            value: "true",
        });
        toast({
            title: "You're on the waitlist!",
            description: "We'll reach out to you soon.",
        });
    }, [segmentTrack, setPreference, account?.email]);

    const ariaLabelPrefix = "Announcement - Ona";
    return (
        <Nudge
            open={open}
            toggleOpen={toggleOpen}
            ariaLabelPrefix={ariaLabelPrefix}
            data-testid="nudge-ona-announcement-trigger"
            data-track-location="ona_announcement_nudge"
        >
            <div
                className="-m-2 flex flex-col gap-2 rounded-2xl p-2"
                style={{
                    background:
                        "linear-gradient(335deg, #1F1329 0%, #333A75 20%, #556CA8 40%, #90A898 60%, #E2B15C 80%, #BEA462 100%)",
                }}
            >
                <div className="p-2 text-center">
                    <div className="mb-2 flex items-center justify-center gap-2">
                        <Text className="text-base font-bold text-white">
                            Introducing <span className="sr-only">Ona</span>
                        </Text>
                        <OnaWordmark className="h-6 w-16 text-white" aria-hidden="true" />
                    </div>
                    <Text className="text-base text-white">The privacy-first software engineering agent.</Text>
                </div>
                <div className="flex flex-col gap-2">
                    {waitListJoined ? (
                        <Button
                            className="grow"
                            aria-label={`${ariaLabelPrefix} - Learn more`}
                            LeadingIcon={IconExternalLink}
                            asChild
                        >
                            <ExternalLink href="https://ona.com">Learn more</ExternalLink>
                        </Button>
                    ) : (
                        <Button
                            className="grow"
                            aria-label={`${ariaLabelPrefix} - Get early access`}
                            onClick={onJoinWaitlist}
                        >
                            Get early access
                        </Button>
                    )}
                </div>
            </div>
        </Nudge>
    );
};
